import type { PlaybackTransport, VideoTimelineSnapshot } from "@carbon6/c6-frontend-design";
import { useEffect, useRef } from "react";

/**
 * 재생 텔레메트리를 CSS 변수 하나로 전달한다. React 를 다시 그리지 않는다.
 *
 * 재생선은 매 프레임 움직인다. 그때마다 sparkline 수백 개를 가진 트리를 다시 그리면 재생이
 * 끊긴다. 그래서 요소에 변수와 켜짐 표시(`data-playhead`)만 직접 쓴다. c6 가 자기 재생 커서를
 * CSS 변수로 옮기는 것과 같은 방식이다. `map` 이 null 을 주면(길이 모름) 선을 끈다.
 *
 * 요소는 ref 객체가 아니라 값으로 받는다. 그래프가 늦게 마운트되는 자리(그리드 로딩)에서
 * ref 만 보면 마운트 순간을 놓친다. 호출부는 `ref={setElement}` 로 전달한다.
 */
export function usePlaybackCssVar(
  element: HTMLElement | null,
  transport: PlaybackTransport | null,
  name: string,
  map: (snapshot: VideoTimelineSnapshot) => number | null,
): void {
  // 렌더마다 새 함수가 와도 다시 구독하지 않는다. 가장 최근 함수만 호출한다.
  const mapRef = useRef(map);
  mapRef.current = map;

  useEffect(() => {
    if (element === null || transport === null) return;
    const apply = () => {
      const value = mapRef.current(transport.getSnapshot());
      if (value === null) {
        element.style.removeProperty(name);
        element.dataset.playhead = "off";
      } else {
        element.style.setProperty(name, String(value));
        element.dataset.playhead = "on";
      }
    };
    apply();
    const unsubscribe = transport.subscribe(apply);
    return () => {
      unsubscribe();
      element.style.removeProperty(name);
      element.dataset.playhead = "off";
    };
  }, [element, transport, name]);
}
